import { useState, useRef } from 'react';
import {
    Upload,
    FileText,
    Link,
    FileCode,
    Book,
    X,
    Check,
    AlertCircle,
    Loader2,
    Plus,
    Terminal,
    FileUp
} from 'lucide-react';

interface PolicyImportProps {
    isOpen: boolean;
    onClose: () => void;
    onImport: (yaml: string, name: string) => void;
}

type ImportSource = 'yaml' | 'jira' | 'document' | 'template' | 'cli';

// Pre-built policy templates
const POLICY_TEMPLATES = [
    {
        id: 'high-risk-approval',
        name: 'High-Risk Action Approval',
        description: 'Require approval for any action with risk score > 70',
        category: 'Risk Management',
        yaml: `id: high-risk-approval
name: high_risk_approval
description: Require human approval for high-risk actions
enabled: true
priority: 100

conditions:
  riskThreshold: 70

effect: require_approval

approvalConfig:
  requiredApprovers:
    - soc-lead
  approvalTimeout: 3600
  escalationPath:
    - security-manager
`,
    },
    {
        id: 'privileged-access',
        name: 'Privileged Access Control',
        description: 'Restrict user disable/enable operations to work hours',
        category: 'Access Control',
        yaml: `id: privileged-access
name: privileged_access_control
description: Limit privileged identity operations
enabled: true
priority: 90

conditions:
  tools:
    - okta
    - entra_id
  actions:
    - disable_user
    - reset_mfa
    - elevate_privileges
  timeWindows:
    - days: [monday, tuesday, wednesday, thursday, friday]
      startTime: "09:00"
      endTime: "17:00"
      timezone: America/Los_Angeles

effect: require_approval

approvalConfig:
  requiredApprovers:
    - identity-team
  approvalTimeout: 1800
`,
    },
    {
        id: 'read-only-siem',
        name: 'SIEM Read-Only Access',
        description: 'Allow read operations on SIEM tools without approval',
        category: 'Data Access',
        yaml: `id: read-only-siem
name: siem_read_only
description: Auto-allow SIEM read operations
enabled: true
priority: 50

conditions:
  tools:
    - splunk
    - sentinel
    - crowdstrike
  actions:
    - search
    - query
    - get_alerts
    - get_events

effect: allow
`,
    },
    {
        id: 'containment-approval',
        name: 'Containment Action Approval',
        description: 'Require approval for host isolation and quarantine',
        category: 'Incident Response',
        yaml: `id: containment-approval
name: containment_approval
description: Approve before isolating hosts or quarantining files
enabled: true
priority: 95

conditions:
  actions:
    - isolate_host
    - quarantine_file
    - block_ip
    - disable_user

effect: require_approval

approvalConfig:
  requiredApprovers:
    - incident-commander
    - soc-lead
  approvalTimeout: 900
  escalationPath:
    - ciso
`,
    },
    {
        id: 'ticket-auto-create',
        name: 'Auto-Create Tickets',
        description: 'Allow automatic ticket creation for all severity levels',
        category: 'ITSM',
        yaml: `id: ticket-auto-create
name: auto_ticket_creation
description: Auto-allow ticket creation in ITSM systems
enabled: true
priority: 30

conditions:
  tools:
    - jira
    - servicenow
  actions:
    - create_ticket
    - update_ticket
    - add_comment

effect: allow
`,
    },
];


export function PolicyImport({ isOpen, onClose, onImport }: PolicyImportProps) {
    const [activeSource, setActiveSource] = useState<ImportSource>('template');
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [yamlContent, setYamlContent] = useState('');
    const [jiraUrl, setJiraUrl] = useState('');
    const [cliCommands, setCliCommands] = useState('');
    const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    if (!isOpen) return null;

    const sources: { id: ImportSource; label: string; icon: typeof Upload; description: string }[] = [
        { id: 'template', label: 'Templates', icon: Book, description: 'Pre-built policy templates' },
        { id: 'yaml', label: 'YAML File', icon: FileCode, description: 'Upload .yaml or .yml files' },
        { id: 'jira', label: 'Jira SOPs', icon: Link, description: 'Import from Jira tickets' },
        { id: 'document', label: 'Documents', icon: FileText, description: 'Parse PDFs & docs with AI' },
        { id: 'cli', label: 'CLI/Runbook', icon: Terminal, description: 'Convert commands to policy' },
    ];

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            const content = event.target?.result as string;
            setYamlContent(content);
            setError(null);
        };
        reader.onerror = () => setError('Failed to read file');
        reader.readAsText(file);
    };

    const handleImport = async () => {
        setIsProcessing(true);
        setError(null);

        try {
            let yaml = '';
            let name = '';

            switch (activeSource) {
                case 'template':
                    const template = POLICY_TEMPLATES.find(t => t.id === selectedTemplate);
                    if (!template) throw new Error('Please select a template');
                    yaml = template.yaml;
                    name = template.name;
                    break;

                case 'yaml':
                    if (!yamlContent.trim()) throw new Error('Please upload or paste YAML content');
                    yaml = yamlContent;
                    name = 'Imported Policy';
                    break;

                case 'jira':
                    if (!jiraUrl.trim()) throw new Error('Please enter a Jira URL');
                    // Simulate AI parsing of Jira SOP
                    await new Promise(r => setTimeout(r, 1500));
                    yaml = `# Imported from Jira: ${jiraUrl}
id: jira-import-${Date.now()}
name: jira_imported_sop
description: SOP imported from Jira ticket
enabled: true
priority: 50

conditions:
  tools:
    - splunk
    - crowdstrike

effect: require_approval

approvalConfig:
  requiredApprovers:
    - soc-analyst
  approvalTimeout: 1800
`;
                    name = 'Jira SOP Import';
                    break;

                case 'cli':
                    if (!cliCommands.trim()) throw new Error('Please enter CLI commands');
                    // Simulate AI conversion
                    await new Promise(r => setTimeout(r, 1500));
                    yaml = `# Converted from CLI commands
id: cli-runbook-${Date.now()}
name: cli_runbook_policy
description: Policy generated from runbook commands
enabled: true
priority: 60

# Original commands:
# ${cliCommands.split('\n').join('\n# ')}

conditions:
  actions:
    - execute_script
    - run_command

effect: require_approval

approvalConfig:
  requiredApprovers:
    - soc-lead
  approvalTimeout: 600
`;
                    name = 'CLI Runbook Policy';
                    break;

                case 'document':
                    throw new Error('Document upload coming soon - drag & drop PDF/DOCX to parse with AI');
            }

            onImport(yaml, name);
            onClose();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Import failed');
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <>
            {/* Backdrop */}
            <div className="fixed inset-0 bg-black/60 z-[98]" onClick={onClose} />

            {/* Modal */}
            <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] max-h-[80vh] bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl z-[99] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-5 border-b border-slate-800">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-primary-500/20 flex items-center justify-center">
                            <Plus className="w-5 h-5 text-primary-400" />
                        </div>
                        <div>
                            <h2 className="text-lg font-semibold text-white">Import Policy</h2>
                            <p className="text-xs text-slate-500">Add policies from various sources</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-slate-800 rounded-lg">
                        <X className="w-5 h-5 text-slate-400" />
                    </button>
                </div>

                {/* Source Tabs */}
                <div className="flex gap-2 p-4 border-b border-slate-800 overflow-x-auto">
                    {sources.map(source => {
                        const Icon = source.icon;
                        const isActive = activeSource === source.id;
                        return (
                            <button
                                key={source.id}
                                onClick={() => {
                                    setActiveSource(source.id);
                                    setError(null);
                                }}
                                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${isActive
                                    ? 'bg-primary-500/20 text-primary-400 border border-primary-500/30'
                                    : 'text-slate-400 hover:text-white hover:bg-slate-800'
                                    }`}
                            >
                                <Icon className="w-4 h-4" />
                                {source.label}
                            </button>
                        );
                    })}
                </div>

                {/* Content Area */}
                <div className="flex-1 overflow-y-auto p-5">
                    {/* Template Source */}
                    {activeSource === 'template' && (
                        <div className="space-y-3">
                            <p className="text-sm text-slate-400 mb-4">
                                Select a pre-built policy template to get started quickly.
                            </p>
                            {POLICY_TEMPLATES.map(template => (
                                <button
                                    key={template.id}
                                    onClick={() => setSelectedTemplate(template.id)}
                                    className={`w-full text-left p-4 rounded-xl border transition-all ${selectedTemplate === template.id
                                        ? 'bg-primary-500/20 border-primary-500/50'
                                        : 'bg-slate-800/50 border-slate-700/50 hover:border-slate-600'
                                        }`}
                                >
                                    <div className="flex items-start justify-between">
                                        <div>
                                            <span className="text-xs text-slate-500 uppercase tracking-wider">{template.category}</span>
                                            <h4 className="font-medium text-white mt-1">{template.name}</h4>
                                            <p className="text-sm text-slate-400 mt-1">{template.description}</p>
                                        </div>
                                        {selectedTemplate === template.id && (
                                            <Check className="w-5 h-5 text-primary-400" />
                                        )}
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}

                    {/* YAML Upload */}
                    {activeSource === 'yaml' && (
                        <div className="space-y-4">
                            <div
                                onClick={() => fileInputRef.current?.click()}
                                className="border-2 border-dashed border-slate-700 rounded-xl p-8 text-center cursor-pointer hover:border-primary-500/50 hover:bg-slate-800/30 transition-all"
                            >
                                <FileUp className="w-10 h-10 text-slate-500 mx-auto mb-3" />
                                <p className="text-sm text-slate-400">
                                    Drop a YAML file here or <span className="text-primary-400">click to browse</span>
                                </p>
                                <p className="text-xs text-slate-500 mt-1">Supports .yaml and .yml files</p>
                            </div>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".yaml,.yml"
                                onChange={handleFileUpload}
                                className="hidden"
                            />
                            <div className="text-center text-slate-500 text-xs">— or paste YAML directly —</div>
                            <textarea
                                value={yamlContent}
                                onChange={(e) => setYamlContent(e.target.value)}
                                placeholder="Paste your YAML policy here..."
                                className="w-full h-48 bg-slate-800 border border-slate-700 rounded-xl p-4 text-sm font-mono text-slate-300 placeholder-slate-500 focus:outline-none focus:border-primary-500 resize-none"
                            />
                        </div>
                    )}

                    {/* Jira Import */}
                    {activeSource === 'jira' && (
                        <div className="space-y-4">
                            <p className="text-sm text-slate-400">
                                Enter a Jira ticket URL containing SOPs or runbooks. The AI will parse and convert it to a policy.
                            </p>
                            <input
                                type="url"
                                value={jiraUrl}
                                onChange={(e) => setJiraUrl(e.target.value)}
                                placeholder="https://yourcompany.atlassian.net/browse/SEC-123"
                                className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-primary-500"
                            />
                            <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700/50">
                                <h4 className="text-sm font-medium text-white mb-2">Supported Jira Content</h4>
                                <ul className="text-xs text-slate-400 space-y-1">
                                    <li>• Ticket descriptions with SOP steps</li>
                                    <li>• Confluence pages linked in tickets</li>
                                    <li>• Runbook attachments (PDF, DOCX)</li>
                                    <li>• Comments with procedure updates</li>
                                </ul>
                            </div>
                        </div>
                    )}

                    {/* Document Import */}
                    {activeSource === 'document' && (
                        <div className="space-y-4">
                            <div className="border-2 border-dashed border-slate-700 rounded-xl p-8 text-center">
                                <FileText className="w-10 h-10 text-slate-500 mx-auto mb-3" />
                                <p className="text-sm text-slate-400">
                                    Drop PDF, DOCX, or TXT files here
                                </p>
                                <p className="text-xs text-slate-500 mt-1">AI will extract and convert to policy YAML</p>
                            </div>
                            <div className="p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-xl">
                                <div className="flex items-start gap-2">
                                    <AlertCircle className="w-4 h-4 text-yellow-400 mt-0.5" />
                                    <p className="text-xs text-yellow-300">
                                        Document parsing uses AI and may require review. Always validate generated policies before enabling.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* CLI/Runbook Import */}
                    {activeSource === 'cli' && (
                        <div className="space-y-4">
                            <p className="text-sm text-slate-400">
                                Paste CLI commands or runbook steps. AI will analyze and generate a policy.
                            </p>
                            <textarea
                                value={cliCommands}
                                onChange={(e) => setCliCommands(e.target.value)}
                                placeholder={`# Example runbook commands:
aws ec2 stop-instances --instance-ids i-1234567890abcdef0
crowdstrike_cli contain_host --hostname infected-pc
splunk search "index=security sourcetype=auth" | head 100`}
                                className="w-full h-48 bg-slate-800 border border-slate-700 rounded-xl p-4 text-sm font-mono text-slate-300 placeholder-slate-500 focus:outline-none focus:border-primary-500 resize-none"
                            />
                        </div>
                    )}

                    {/* Error Display */}
                    {error && (
                        <div className="mt-4 p-3 bg-red-500/10 border border-red-500/20 rounded-xl flex items-center gap-2 text-red-400 text-sm">
                            <AlertCircle className="w-4 h-4" />
                            {error}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 p-5 border-t border-slate-800">
                    <button onClick={onClose} className="btn btn-ghost">
                        Cancel
                    </button>
                    <button
                        onClick={handleImport}
                        disabled={isProcessing}
                        className="btn btn-primary flex items-center gap-2"
                    >
                        {isProcessing ? (
                            <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Processing...
                            </>
                        ) : (
                            <>
                                <Check className="w-4 h-4" />
                                Import Policy
                            </>
                        )}
                    </button>
                </div>
            </div>
        </>
    );
}
